<script>
import emojis from './emojisGroup.json';

const RECENTS_KEY = 'wlab_emoji_recents_v1';
const EMOJI_GROUPS = Object.freeze(emojis);
const EMOJI_TABS = Object.freeze([
  { key: 'recent', testId: 'emoji-tab', icon: 'clock' },
  { key: 'smileys_emotion', testId: 'emoji-tab', icon: 'smile' },
  { key: 'people_body', testId: 'emoji-tab', icon: 'person' },
  { key: 'animals_nature', testId: 'emoji-tab', icon: 'animals' },
  { key: 'food_drink', testId: 'emoji-tab', icon: 'food' },
  { key: 'travel_places', testId: 'emoji-tab', icon: 'travel' },
  { key: 'activities', testId: 'emoji-tab', icon: 'activities' },
  { key: 'objects', testId: 'emoji-tab', icon: 'objects' },
]);
const EMOJI_CATEGORY_MAP = new Map(
  EMOJI_GROUPS.map(category => [category.slug, category])
);
const DEFAULT_CATEGORY =
  EMOJI_CATEGORY_MAP.get('smileys_emotion') || EMOJI_GROUPS[0] || null;
const SEARCH_CACHE = new Map();
const ALL_EMOJIS_FLAT = EMOJI_GROUPS.flatMap(category =>
  (category.emojis || []).map(e => ({
    ...e,
    category: category.name,
    categorySlug: category.slug,
  }))
);
const ALL_EMOJIS_INDEXED = ALL_EMOJIS_FLAT.map(e => ({
  ...e,
  slugSearch: String(e.slug || '')
    .replaceAll('_', ' ')
    .toLowerCase(),
  nameSearch: String(e.name || '').toLowerCase(),
}));

function safeParseJSON(value, fallback) {
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch (e) {
    return fallback;
  }
}

export default {
  name: 'WlabEmojiPicker',
  props: {
    onSelect: { type: Function, default: () => {} },
  },
  data() {
    return {
      activeTab: 'recent',
      search: '',
      searchDebounced: '',
      searchDebounceTimer: null,
      recents: [],
    };
  },
  computed: {
    tabs() {
      return EMOJI_TABS;
    },
    normalizedSearch() {
      return String(this.searchDebounced || '')
        .trim()
        .toLowerCase();
    },
    searchResults() {
      const q = this.normalizedSearch;
      if (!q) return [];
      if (SEARCH_CACHE.has(q)) return SEARCH_CACHE.get(q);
      const results = ALL_EMOJIS_INDEXED.filter(
        e => e.slugSearch.includes(q) || e.nameSearch.includes(q)
      );
      if (SEARCH_CACHE.size > 120) SEARCH_CACHE.clear();
      SEARCH_CACHE.set(q, results);
      return results;
    },
    frequentEmojis() {
      return this.recents.slice(0, 18);
    },
    activeCategory() {
      if (this.activeTab === 'recent') return null;
      return EMOJI_CATEGORY_MAP.get(this.activeTab) || null;
    },
    displayCategory() {
      if (this.activeTab === 'recent') {
        return DEFAULT_CATEGORY;
      }
      return this.activeCategory || DEFAULT_CATEGORY;
    },
    displayEmojis() {
      return this.displayCategory?.emojis || [];
    },
    virtualGridItems() {
      return this.normalizedSearch ? this.searchResults : this.displayEmojis;
    },
  },
  mounted() {
    this.loadRecents();
    // Default to Smileys (matches common UX)
    this.activeTab = this.recents.length ? 'recent' : 'smileys_emotion';
    this.searchDebounced = this.search;
  },
  beforeUnmount() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  },
  methods: {
    onSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }

      const nextSearch = String(this.search || '');
      if (!nextSearch.trim()) {
        // Apply empty query immediately for snappy clear.
        this.searchDebounced = nextSearch;
        return;
      }

      this.searchDebounceTimer = setTimeout(() => {
        this.searchDebounced = nextSearch;
        this.searchDebounceTimer = null;
      }, 120);
    },
    loadRecents() {
      const raw = window?.localStorage?.getItem(RECENTS_KEY);
      const parsed = safeParseJSON(raw, []);
      this.recents = Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    },
    persistRecents(next) {
      this.recents = next;
      try {
        window?.localStorage?.setItem(RECENTS_KEY, JSON.stringify(next));
      } catch (e) {
        // ignore
      }
    },
    pickEmoji(emoji, slug) {
      this.onSelect?.(emoji);
      const entry = { emoji, slug };
      const next = [entry, ...this.recents.filter(r => r.slug !== slug)].slice(
        0,
        36
      );
      this.persistRecents(next);
    },
    setTab(key) {
      this.activeTab = key;
    },
    iconSvg(icon) {
      // Inline icons to match the reference layout closely.
      switch (icon) {
        case 'search':
          return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20"><path stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10a7 7 0 1 0 14 0 7 7 0 0 0-14 0M21 21l-6-6"></path></svg>`;
        case 'clock':
          return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20"><path stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 7v5l3 3M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"></path></svg>`;
        case 'smile':
          return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20"><path stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 10h.01M15 10h.01M9.5 15a3.5 3.5 0 0 0 5 0M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"></path></svg>`;
        case 'person':
          return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20"><path stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19V9M9 3l3 2 3-2m-3 15.176a3 3 0 1 1-4.953-2.449l-.025.023A4.502 4.502 0 0 1 8.505 7c1.414 0 2.675.652 3.5 1.671a4.5 4.5 0 1 1 4.983 7.079A3 3 0 1 1 12.005 18z"></path></svg>`;
        case 'animals':
          return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20"><path stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 14c.83.642 2.077 1.017 3.5 1 1.423.017 2.67-.358 3.5-1s2.077-1.017 3.5-1c1.423-.017 2.67.358 3.5 1M8 3a2.4 2.4 0 0 0-1 2 2.4 2.4 0 0 0 1 2m4-4a2.4 2.4 0 0 0-1 2 2.4 2.4 0 0 0 1 2m4.746 9.726a3 3 0 1 0 .252-5.555M3 10h14v5a6 6 0 0 1-6 6H9a6 6 0 0 1-6-6z"></path></svg>`;
        case 'food':
          return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20"><path stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 3C8.82 3 3 8.82 3 16M16 3a5 5 0 0 1 5 5m-5-5a5 5 0 0 0 5 5M3 16a5 5 0 0 0 5 5m-5-5a5 5 0 0 1 5 5m0 0c7.18 0 13-5.82 13-13m-6 1-6 6m3-5 2 2m-4 0 2 2"></path></svg>`;
        case 'travel':
          return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20"><path stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m14.5 6.5 3-2.9a2.05 2.05 0 0 1 2.9 2.9l-2.9 3L20 17l-2.5 2.55L14 13l-3 3v3l-2 2-1.5-4.5L3 15l2-2h3l3-3-6.5-3.5L7 4z"></path></svg>`;
        case 'activities':
          return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20"><path stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12h1m8-9v1m8 8h1M5.6 5.6l.7.7m12.1-.7-.7.7M9 16a5 5 0 1 1 6 0 3.5 3.5 0 0 0-1 3 2 2 0 0 1-4 0 3.5 3.5 0 0 0-1-3M9.7 17h4.6"></path></svg>`;
        case 'objects':
        default:
          return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20"><path fill="currentcolor" d="M10 5H4c-.55 0-1 .45-1 1s.45 1 1 1h2v3c0 .55.45 1 1 1s1-.45 1-1V7h2c.55 0 1-.45 1-1s-.45-1-1-1m0-3H4c-.55 0-1 .45-1 1s.45 1 1 1h6c.55 0 1-.45 1-1s-.45-1-1-1"></path></svg>`;
      }
    },
  },
};
</script>

<template>
  <div data-testid="emoji-picker" class="css-viin2l" @click.stop>
    <div data-testid="emoji-tabs" class="css-ffyrjc">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        data-testid="emoji-tab"
        type="button"
        :class="activeTab === tab.key ? 'css-71o0uh' : 'css-aq5qku'"
        @click="setTab(tab.key)"
      >
        <span
          class="lc-Icon-module__icon___J5RH5"
          :class="
            activeTab === tab.key
              ? 'lc-Icon-module__icon--action-primary___iKKt4'
              : 'lc-Icon-module__icon--primary___lclud'
          "
          v-html="iconSvg(tab.icon)"
        />
      </button>
    </div>

    <div class="css-x1ifoo">
      <div
        role="search"
        class="lc-SearchInput-module__search-input___3J4w9 lc-SearchInput-module__search-input--medium___c3OEc"
      >
        <span
          class="lc-SearchInput-module__search-input__search-icon___KYne6 lc-SearchInput-module__search-input__search-icon--medium___exSsN lc-Icon-module__icon___J5RH5 lc-Icon-module__icon--primary___lclud"
          v-html="iconSvg('search')"
        />
        <div
          class="lc-Typography-module__paragraph-md___Ogs8k lc-SearchInput-module__search-input__input-wrapper___9NT39 lc-SearchInput-module__search-input__input-wrapper--medium___VN8Dh lc-SearchInput-module__search-input__input-wrapper--crop___JA7J1"
        >
          <input
            v-model="search"
            role="searchbox"
            type="text"
            :placeholder="$t('EMOJI.PLACEHOLDER')"
            class="no-margin"
            @input="onSearchInput"
          />
        </div>
      </div>
    </div>

    <div
      id="emoji_section_wrapper_id"
      class="css-1wwmjn8"
      :class="{ 'is-searching': normalizedSearch }"
    >
      <div v-if="normalizedSearch" class="css-ugv1oc">
        <h6 class="css-1bcz0cl">Search Result</h6>
        <div class="wlab-emoji-scroller">
          <div class="wlab-emoji-grid">
            <button
              v-for="item in virtualGridItems"
              :key="`search-${item.slug}`"
              data-testid="emoji-button"
              type="button"
              class="css-ycdt0h"
              @click="pickEmoji(item.emoji, item.slug)"
            >
              {{ item.emoji }}
              <span class="css-1mn5rql">{{ item.slug }}</span>
            </button>
          </div>
        </div>
      </div>

      <div v-else class="css-ugv1oc">
        <h6 class="css-1bcz0cl">Frequently Used</h6>
        <div class="css-rtde4j">
          <button
            v-for="item in frequentEmojis"
            :key="item.slug"
            data-testid="emoji-button"
            type="button"
            class="css-ycdt0h"
            @click="pickEmoji(item.emoji, item.slug)"
          >
            {{ item.emoji }}
            <span class="css-1mn5rql">{{ item.slug }}</span>
          </button>
        </div>
      </div>

      <div v-if="!normalizedSearch" class="css-ugv1oc">
        <h6 class="css-1bcz0cl">{{ displayCategory?.name || 'Emojis' }}</h6>
        <div class="wlab-emoji-scroller">
          <div class="wlab-emoji-grid">
            <button
              v-for="item in virtualGridItems"
              :key="item.slug"
              data-testid="emoji-button"
              type="button"
              class="css-ycdt0h"
              @click="pickEmoji(item.emoji, item.slug)"
            >
              {{ item.emoji }}
              <span class="css-1mn5rql">{{ item.slug }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
[class^='lc-'] {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Helvetica Neue',
    Arial,
    sans-serif;
}

.css-viin2l {
  width: 312px;
  min-height: 100px;
  position: relative;
  border-radius: 4px;
  box-shadow: var(--shadow-pop-over, 0 6px 20px 0 rgb(19 19 23 / 30%));
  background-color: #fff;
  border: 1px solid rgba(15, 23, 42, 0.12);
}

.css-ffyrjc {
  display: flex;
  border-bottom: 1px solid rgba(15, 23, 42, 0.12);
  padding: 2px 0 2px 8px;
}

.css-71o0uh,
.css-aq5qku {
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  background: transparent;
  padding: 7px 2px 8px;
  margin: 0 6px -1px;
  outline: none;
  border: 0;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  box-shadow: none;
}

.css-71o0uh {
  border-color: #0059e1;
}

.lc-Icon-module__icon___J5RH5 {
  display: flex;
  align-items: center;
}
.lc-Icon-module__icon--action-primary___iKKt4 {
  color: #0059e1;
}
.lc-Icon-module__icon--primary___lclud {
  color: #131317;
}
.lc-Icon-module__icon___J5RH5 :deep(svg) {
  pointer-events: none;
}

.css-x1ifoo {
  margin: 8px 14px 0;
}

.lc-SearchInput-module__search-input___3J4w9 {
  box-sizing: border-box;
  display: flex;
  position: relative;
  align-items: center;
  transition: all 0.25s ease-in-out;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  background-color: #fff;
  width: 100%;
  overflow: hidden;
  color: #131317;
  margin-bottom: 0 !important;
}
.lc-SearchInput-module__search-input--medium___c3OEc {
  height: 36px;
}
.lc-SearchInput-module__search-input__search-icon___KYne6 {
  position: absolute;
  justify-content: center;
  height: 100%;
  display: flex;
  align-items: center;
}
.lc-SearchInput-module__search-input__search-icon--medium___exSsN {
  width: 36px;
}
.lc-SearchInput-module__search-input__input-wrapper___9NT39 {
  flex: 1 1;
  padding: 0;
  width: 100%;
}
.lc-SearchInput-module__search-input__input-wrapper--medium___VN8Dh {
  padding-left: 36px;
}
.lc-SearchInput-module__search-input__input-wrapper___9NT39 input {
  border: 0;
  background-color: transparent;
  padding: 0;
  width: 100%;
  color: inherit;
  outline: none;
  font-size: 14px;
  margin: 0 !important;
}

.css-1wwmjn8 {
  height: 330px;
  overflow: hidden;
  padding: 0 8px 25px;
}

.css-ugv1oc {
  padding: 12px 0 0;
}

.css-1bcz0cl {
  font-size: 13px;
  font-weight: 600;
  color: rgba(19, 19, 23, 0.7);
  margin-bottom: 8px;
  margin-left: 6px;
}

.css-rtde4j {
  display: flex;
  flex-wrap: wrap;
}

.wlab-emoji-scroller {
  height: 251px;
  overflow: auto;
  display: block;
  width: 100%;
}

.wlab-emoji-grid {
  display: grid;
  grid-template-columns: repeat(9, minmax(0, 1fr));
  align-items: start;
}

.is-searching .wlab-emoji-scroller {
  height: 251px;
}

.wlab-emoji-scroller .css-ycdt0h {
  width: 100%;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
}

.css-ycdt0h {
  background: none;
  border-radius: 4px;
  border: none;
  box-sizing: content-box;
  font-size: 18px;
  line-height: 24px;
  margin: 1px;
  outline: none;
  padding: 4px;
  position: relative;
  text-align: center;
  height: 24px;
  width: 24px;
}
.css-ycdt0h:hover {
  background: rgba(19, 19, 23, 0.06);
}

.css-1mn5rql {
  display: none;
  background-color: #131317;
  border-radius: 4px;
  color: #fff;
  bottom: -19px;
  left: 50%;
  position: absolute;
  line-height: 12px;
  font-size: 12px;
  padding: 5px 7px;
  z-index: 2010;
  white-space: nowrap;
  transform: translate(-50%);
}
.css-ycdt0h:hover .css-1mn5rql {
  display: block;
}
</style>
